import { type FC } from 'react';

import {
  Icon as ChakraIcon,
  type IconProps as ChakraIconProps,
  Circle,
  type CircleProps,
  type ComponentWithAs,
} from '@chakra-ui/react';

type CircularIconContainerProps = Omit<CircleProps, 'size'> & {
  /** The icon component to display */
  icon:
    | ComponentWithAs<'svg', ChakraIconProps>
    | FC<React.PropsWithChildren<unknown>>;
  /** Size of the container in pixels (default: 40) */
  size?: number;
  /** Size of the icon (default: auto-calculated as 60% of container size) */
  iconSize?: number;
  /** Background color of the container (default: 'white') */
  backgroundColor?: string;
  /** Icon color (default: '{gradientColor}.500') */
  iconColor?: string;
  /** Base color for the gradient border (default: 'brand') */
  gradientColor?: string;
  /** Width of the gradient border effect in pixels (default: 2) */
  gradientBorderWidth?: number;
  /** Enable/disable gradient border effect (default: true) */
  showGradientBorder?: boolean;
};

export const CircularIconContainer: React.FC<CircularIconContainerProps> = ({
  icon: IconComponent,
  size = 40,
  iconSize,
  backgroundColor = 'white',
  iconColor,
  gradientColor = 'brand',
  gradientBorderWidth = 1,
  showGradientBorder = true,
  ...props
}) => {
  // Calculate sizes for gradient border layers
  const gradientWidth = (size / 100) * 40;
  const outerSize = `${size + gradientWidth + gradientBorderWidth * 2}px`;
  const middleSize = `${size + gradientWidth + gradientBorderWidth}px`;

  // Auto-calculate icon size as 60% of container if not provided
  const calculatedIconSize = iconSize ?? `${Math.round(size * 0.6)}px`;

  // Use provided icon color or default to gradient color
  const finalIconColor = iconColor ?? `${gradientColor}.500`;

  if (!showGradientBorder) {
    // Simple version without gradient border
    return (
      <Circle
        size={`${size}px`}
        bgColor={backgroundColor}
        fontSize={calculatedIconSize}
        {...props}
      >
        <ChakraIcon as={IconComponent} color={finalIconColor} />
      </Circle>
    );
  }

  // Gradient border version using nested circles
  // This technique creates a gradient border effect since CSS borders don't support gradients
  return (
    <Circle
      bgGradient={`linear(to-b, ${gradientColor}.100 40%, ${backgroundColor} 75%)`}
      size={outerSize}
      {...props}
    >
      <Circle
        bgGradient={`linear(to-b, ${gradientColor}.50 40%, ${backgroundColor} 75%)`}
        size={middleSize}
      >
        <Circle
          borderColor={`${gradientColor}.100`}
          borderWidth={gradientBorderWidth}
          size={`${size}px`}
          bgColor={backgroundColor}
          fontSize={calculatedIconSize}
        >
          <ChakraIcon as={IconComponent} color={finalIconColor} />
        </Circle>
      </Circle>
    </Circle>
  );
};
